import type { FC, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const controlClass = cn(
    'w-full rounded-[10px] border border-ink/15 bg-paper/80 px-3.5 text-[14px] text-ink placeholder:text-ink/35',
    'shadow-[inset_0_1px_2px_rgb(40_38_34/0.06)] transition-[border-color,background-color,box-shadow] duration-300 ease-soft',
    'hover:border-ink/25 focus:border-ink/45 focus:bg-paper focus:outline-none focus:ring-4 focus:ring-cloud/60',
    'aria-[invalid=true]:border-clay-deep/60'
)

interface FieldShellProps {
    id: string
    label: string
    hint?: string
    error?: string
    optional?: boolean
    children: ReactNode
    className?: string
}

/** Label, control, hint and error, wired together for screen readers. */
export const FieldShell: FC<FieldShellProps> = ({ id, label, hint, error, optional, children, className }): ReactNode => {
    return (
        <div className={cn('flex flex-col', className)}>
            <label htmlFor={id} className='mb-2 flex items-baseline justify-between gap-4 text-[13px] font-medium text-ink'>
                {label}
                {optional && <span className='text-[12px] font-normal text-ink/45'>Optional</span>}
            </label>

            {children}

            {hint && !error && (
                <p id={`${id}-hint`} className='mt-2 text-[12.5px] leading-snug text-ink/55'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${id}-error`} className='mt-2 text-[12.5px] leading-snug text-clay-deep'>
                    {error}
                </p>
            )}
        </div>
    )
}

const describedBy = (id: string, hint?: string, error?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`

    return undefined
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
    id: string
    label: string
    hint?: string
    error?: string
    optional?: boolean
    className?: string
}

export const TextField: FC<TextFieldProps> = ({ id, label, hint, error, optional, className, ...input }): ReactNode => {
    return (
        <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} className={className}>
            <input
                id={id}
                name={id}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy(id, hint, error)}
                className={cn(controlClass, 'h-11')}
                {...input}
            />
        </FieldShell>
    )
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
    id: string
    label: string
    hint?: string
    error?: string
    optional?: boolean
    className?: string
}

export const TextAreaField: FC<TextAreaFieldProps> = ({ id, label, hint, error, optional, className, rows = 4, ...input }): ReactNode => {
    return (
        <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} className={className}>
            <textarea
                id={id}
                name={id}
                rows={rows}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy(id, hint, error)}
                className={cn(controlClass, 'resize-y py-3 leading-[1.6]')}
                {...input}
            />
        </FieldShell>
    )
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
    id: string
    label: string
    options: string[]
    placeholder?: string
    hint?: string
    error?: string
    optional?: boolean
    className?: string
}

export const SelectField: FC<SelectFieldProps> = ({
    id,
    label,
    options,
    placeholder = 'Choose one',
    hint,
    error,
    optional,
    className,
    ...select
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} hint={hint} error={error} optional={optional} className={className}>
            <div className='relative'>
                <select
                    id={id}
                    name={id}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy(id, hint, error)}
                    className={cn(controlClass, 'h-11 cursor-pointer appearance-none pr-10')}
                    {...select}
                >
                    <option value=''>
                        {placeholder}
                    </option>
                    {options.map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>

                <ChevronDown aria-hidden='true' strokeWidth={1.75} className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-ink/50' />
            </div>
        </FieldShell>
    )
}

interface ChoiceGroupProps {
    name: string
    legend: string
    options: string[]
    value: string[]
    onChange: (value: string[]) => void
    multiple?: boolean
    hint?: string
    error?: string
    optional?: boolean
    className?: string
}

/** Pill shaped checkboxes or radios. */
export const ChoiceGroup: FC<ChoiceGroupProps> = ({
    name,
    legend,
    options,
    value,
    onChange,
    multiple = false,
    hint,
    error,
    optional,
    className
}): ReactNode => {
    const toggle = (option: string) => {
        if (!multiple) {
            onChange([option])

            return
        }

        onChange(value.includes(option) ? value.filter(item => item !== option) : [...value, option])
    }

    return (
        <fieldset
            aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
            className={cn('flex flex-col', className)}
        >
            <legend className='mb-3 flex w-full items-baseline justify-between gap-4 text-[13px] font-medium text-ink'>
                {legend}
                {optional && <span className='text-[12px] font-normal text-ink/45'>Optional</span>}
            </legend>

            <div className='flex flex-wrap gap-2'>
                {options.map(option => {
                    const checked = value.includes(option)

                    return (
                        <label
                            key={option}
                            className={cn(
                                'relative inline-flex h-9 cursor-pointer select-none items-center rounded-full border px-3.5 text-[13px]',
                                'transition-[background-color,border-color,color] duration-300 ease-soft has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-cloud/70',
                                checked ? 'border-ink bg-ink text-paper' : 'border-ink/15 bg-paper/70 text-ink/80 hover:border-ink/30'
                            )}
                        >
                            <input
                                type={multiple ? 'checkbox' : 'radio'}
                                name={name}
                                value={option}
                                checked={checked}
                                onChange={() => toggle(option)}
                                className='sr-only'
                            />
                            {option}
                        </label>
                    )
                })}
            </div>

            {hint && !error && (
                <p id={`${name}-hint`} className='mt-2.5 text-[12.5px] leading-snug text-ink/55'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${name}-error`} className='mt-2.5 text-[12.5px] leading-snug text-clay-deep'>
                    {error}
                </p>
            )}
        </fieldset>
    )
}
